import { useArchiveConfirmationModal } from '@/components/ConfirmationModal';
import { CopyButton } from '@/components/CopyButton';
import { useErrorInline } from '@/context/InlineErrorContext/InlineErrorContext';
import { ReceivedInvoiceDetail } from '@/context/services/invoice/type';
import { useCreateInvoiceReportCallbackNavigation } from '@/context/services/invoice_report/InvoiceReport.service';
import { useWrapperClick } from '@/features/invoices/components/Details/Components/Right/HeaderButton/hooks';
import {
  useCallArchive,
  useCallArchiveCallback,
  useCallRestoreArchive,
  useCallRestoreArchiveCallback,
} from '@/features/invoices/components/Details/Components/Right/hooks/useArchive';
import { useCreateInvoiceReport } from '@/features/invoices/components/Details/Components/Right/hooks/useInvoiceReport';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useMakeCopyIdURL } from '@/hooks/useMakeCopyURL';
import { useTranslation } from '@/i18n';
import { isString } from '@/libs/typeguard/string';
import {
  ButtonGroup,
  useNotification,
} from '@moneyforward/ap-frontend-components';
import { FeatureFlagComponent } from 'packages-featureflags';
import { FC, memo, useCallback, useMemo } from 'react';
import { ArchiveButton } from './ArchiveButton';
import { CreateInvoiceReportButton } from './CreateInvoiceReport';
import { RestoreArchiveButton } from './RestoreButton';

type HeaderButton = {
  onArchiveClick: () => void;
  onArchiveRestoreClick: () => Promise<void>;
  onCreateInvoiceReportsClick: () => Promise<void>;
  copyText?: string;
  isScannerRequirementSatisfied?: boolean;
};

export type Props = {
  data: ReceivedInvoiceDetail;
};

export const HeaderButtons: FC<Props> = memo(({ data }) => {
  const { t } = useTranslation();
  const modal = useArchiveConfirmationModal();
  const notify = useNotification();
  const { setError } = useErrorInline();
  // Cancel
  const onCancelClick = useCallback(() => {
    modal.close();
  }, [modal]);

  // To archive
  const handleArchiveCallback = useCallArchiveCallback(modal, notify);
  const onMovingArchive = useCallArchive(
    data.id!,
    handleArchiveCallback,
    setError
  );

  const onArchiveClick = useCallback(() => {
    modal.open({
      title: t('invoice_archive_dialog_title'),
      fileName: data.filename ?? '',
      pics: data.pics?.map((item) => item.name).filter(isString) || [],
      onCancelClick: onCancelClick,
      onArchiveClick: onMovingArchive,
      size: 'md',
      testId: 'archive-modal',
    });
  }, [modal, data.filename, data.pics, onCancelClick, onMovingArchive, t]);

  // To Restore archive
  const handleRestoreArchiveCallback = useCallRestoreArchiveCallback(notify);
  const onRestoreArchiveClick = useCallRestoreArchive(
    data.id!,
    handleRestoreArchiveCallback,
    setError
  );

  // To Invoice report
  const handleInvoiceReportCallback =
    useCreateInvoiceReportCallbackNavigation();
  const onCreateInvoiceReportClick = useCreateInvoiceReport(
    data.id!,
    handleInvoiceReportCallback,
    setError
  );
  const copyIdUrl = useMakeCopyIdURL(`IV-${data.searchable_number}`);

  return (
    <HeaderButtonsInner
      onArchiveClick={onArchiveClick}
      onArchiveRestoreClick={onRestoreArchiveClick}
      onCreateInvoiceReportsClick={onCreateInvoiceReportClick}
      copyText={copyIdUrl}
      isScannerRequirementSatisfied={data.scanner_save_requirement_satisfied}
    />
  );
});
HeaderButtons.displayName = 'HeaderButtons';

export const HeaderButtonsInner: FC<HeaderButton> = ({
  onArchiveClick,
  onArchiveRestoreClick,
  onCreateInvoiceReportsClick,
  copyText,
  isScannerRequirementSatisfied,
}) => {
  const { t } = useTranslation();
  const { isDisabled, onClick } = useWrapperClick({
    onArchiveClick,
    onArchiveRestoreClick,
    onCreateInvoiceReportsClick,
  });
  const flag = useGetFlags('RECEIVED_INVOICE_COPY_BUTTON');
  const size = useMemo(() => (flag ? 'sm' : 'md'), [flag]);
  const copyMessages = useMemo(
    () => ({
      coping: t('received_invoice_url_copy_text'),
      copied: t('received_invoice_url_copied_text'),
    }),
    [t]
  );

  return (
    <ButtonGroup>
      <FeatureFlagComponent flagName='RECEIVED_INVOICE_COPY_BUTTON'>
        <CopyButton
          size={size}
          text={copyText ?? ''}
          show
          copyTooltip={copyMessages.coping}
          copiedTooltip={copyMessages.copied}
        />
      </FeatureFlagComponent>
      <ArchiveButton
        onClick={onClick('archive')}
        size={size}
        isDisabled={isDisabled}
      />
      <CreateInvoiceReportButton
        onClick={onClick('invoiceReport')}
        size={size}
        isDisabled={isDisabled}
        isScannerRequirementSatisfied={isScannerRequirementSatisfied}
      />
      <RestoreArchiveButton
        onClick={onClick('restore')}
        size={size}
        isDisabled={isDisabled}
      />
    </ButtonGroup>
  );
};
